import type { ProjectDto } from '@aiai/shared';

type ProjectLike = Pick<ProjectDto, 'id' | 'name' | 'kind'> & { code?: string | null };

/** Route for the project or prototype detail page. */
export function projectHref(kind: ProjectDto['kind'], projectId: string): string {
  return kind === 'prototype' ? `/prototypes/${projectId}` : `/projects/${projectId}`;
}

/**
 * A prototype IS a Project (kind === 'prototype') but lives under /prototypes,
 * not /projects. Shared issue/run pages use this to build the right breadcrumb
 * root + self link + labels for whichever kind they're rendering. Links use the
 * project's pretty code when it has one, so URLs never show the raw UUID.
 */
export function projectNav(project?: ProjectLike | null) {
  const isPrototype = project?.kind === 'prototype';
  const ref = project?.code ?? project?.id;
  const rootLabel = isPrototype ? 'Prototypes' : 'Projects';
  const rootTo = isPrototype ? '/prototypes' : '/projects';
  const selfTo = ref
    ? isPrototype
      ? `/prototypes/${ref}`
      : `/projects/${ref}`
    : rootTo;
  const name = project?.name ?? (isPrototype ? 'Prototype' : 'Project');
  return { isPrototype, rootLabel, rootTo, selfTo, name };
}

/** Route for authoring a new local issue under a project or prototype. */
export function newIssueHref(kind: ProjectDto['kind'], projectId: string): string {
  return kind === 'prototype'
    ? `/prototypes/${projectId}/issues/new`
    : `/projects/${projectId}/issues/new`;
}

/** Route for the loops list page under a project or prototype. */
export function loopsHref(kind: ProjectDto['kind'], projectId: string): string {
  return kind === 'prototype'
    ? `/prototypes/${projectId}/loops`
    : `/projects/${projectId}/loops`;
}

/** Route for editing a specific loop under a project or prototype. */
export function loopEditHref(kind: ProjectDto['kind'], projectId: string, loopId: string): string {
  return kind === 'prototype'
    ? `/prototypes/${projectId}/loops/${loopId}/edit`
    : `/projects/${projectId}/loops/${loopId}/edit`;
}
